import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { ReactWaves } from "components/ReactWaves";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { WaveSurfer } from "types";
import { AudioPlayerContainerSC, AudioPlayerWrapperSC, WaveWrapperSC } from "./styled";
import { LocalStorageKeys } from "../../../../../providers/AudioProvider/types";
import { forcePreservesPitch, Peaks } from "utils/audioPeaks";
import { loadOriginalPeaks, ORIGINAL_PEAKS_BUCKETS, restorePlayhead } from "utils/originalPeaks";
import { usePlaybackGate } from "hooks/usePlaybackGate";
import { registerOriginalMedia } from "utils/originalMedia";
import { announceUserSeek } from "utils/playbackIntent";
import { guardSeekTo } from "utils/safeWavesurfer";

/**
 * How long to wait for the original's peaks before drawing a flat track anyway.
 *
 * Generous, because giving up early is not free: peaks that arrive after it can only be drawn by
 * reloading the element, which waits for a pause (see the late-peaks effect below). The server
 * usually answers in well under a second, and the decode fallback jumps the queue ahead of every
 * cue (utils/audioPeaks), so reaching this at all is the unusual case.
 */
const PEAKS_DEADLINE_MS = 15000;

export function AudioTrackOriginal() {
    const wavesContainerRef = useRef<HTMLDivElement | null>(null);
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const zoomLevel = 100 - state["zoomLevel"];

    const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
    const [wsReady, setWsReady] = useState(false);
    const [pos, setPos] = useState(0);
    // Peaks come from the editor server, or are decoded by us — never by the backend; see
    // utils/originalPeaks and utils/audioPeaks. Without them the MediaElement backend (the one that
    // preserves pitch) would draw a flat line.
    const [peaks, setPeaks] = useState<Peaks>([]);
    /**
     * DRAW ONLY ONCE THE PEAKS ARE KNOWN.
     *
     * react-waves reloads the track whenever the `audioPeaks` prop changes identity
     * (dist/index.es.js:9326), and reloading builds a NEW <audio>, throws the old one away and,
     * on ready, seeks to the `pos` prop. Handing peaks over later therefore restarts the master
     * element mid-playback and drops it at a stale position. Mounting after they have settled
     * means the prop does not change and the reload does not happen — with one exception: peaks
     * that missed the deadline, applied once, only while nothing plays (`latePeaks` below).
     */
    const [peaksSettled, setPeaksSettled] = useState(false);
    /**
     * Latched the instant the track is drawn: from then on peaks reach the track only through
     * `latePeaks`, never straight from a request that has just finished.
     */
    const settledRef = useRef(false);
    /**
     * ОПОЗДАВШИЕ ПИКИ НЕ ВЫБРАСЫВАЮТСЯ (4119e839, папка 21, 02.10.2026).
     *
     * Раньше пики, пришедшие после срока, терялись до конца сессии: переводчица на медленном
     * ноутбуке видела плоскую русскую дорожку («нет аудио дорожки на русском»), в Chrome у её
     * холстов через минуту было 0 закрашенных точек. Теперь они ждут здесь первой минуты, когда
     * ничего не играет, и рисуются один раз — с возвратом playhead на то же место.
     *
     * `drawnWithoutRef` — дорожка нарисована по сроку, без своих пиков; `lateAppliedRef` — опоздавшие
     * уже отданы (не больше одного раза на источник); `restoreRef` — куда вернуть новый элемент.
     */
    const [latePeaks, setLatePeaks] = useState<Peaks | null>(null);
    const drawnWithoutRef = useRef(false);
    const lateAppliedRef = useRef(false);
    const restoreRef = useRef<{ from: HTMLMediaElement | null; at: number | null } | null>(null);
    /** Перепроверить опоздавшие пики, когда элемент, игравший без state.playing, встал. */
    const [lateRecheck, setLateRecheck] = useState(0);
    /** Чьи пики спрашивать у сервера — читается в момент смены mp3 и в момент ответа. */
    const sourceRef = useRef({ mediaId: "", folder: 0, liveStream: false, audioDurationMs: 0 });
    sourceRef.current = {
        mediaId: state.mediaId,
        folder: state.folder,
        liveStream: Boolean(state.manualOnlyStream),
        audioDurationMs: state.audioDuration || 0,
    };

    const [playbackTime, setPlaybackTime] = useState(0);

    /**
     * A voicing is being waited for, or is starting: the lecturer does not move.
     *
     * The alternative was to skip the voicing forward into the middle of its own sentence, which
     * is what made playback jumpy. A voicing has to start at its beginning; it is a sentence. So
     * the original gives instead, and the wait lands in the gap between two cues where nothing is
     * being said in either language.
     *
     * The same hook drives the video (see VideoPlayer/components/Player). That is the point of
     * having a hook at all: this track used to be the only subscriber, so the picture ran on
     * through every wait and drifted a little further ahead each time.
     *
     * Clearing the claims when playback stops belongs to useVoicingSchedule, not here — a medium
     * that the gate holds is the wrong place to decide when holds end.
     */
    const held = usePlaybackGate();

    /**
     * Make this instance safe to drive, whichever event hands it over.
     *
     * Stopping the track the moment a voicing needs to start means play() and pause() sometimes
     * land in the same frame, and the browser rejects the interrupted play() promise. Nothing is
     * wrong when that happens — the pause is the intended outcome — so the rejection is swallowed.
     *
     * It is installed from `ready` as well as `loading` because `loading` is emitted by the decode
     * read that supplying `audioPeaks` exists to prevent: this track always passes peaks, so that
     * event never fires and this was never installed at all. That is where the console's
     * "AbortError: The play() request was interrupted by a call to pause()" came from.
     */
    const harden = (wavesurfer: WaveSurfer) => {
        const instance = wavesurfer as any;
        if (instance.__playHardened) return;
        instance.__playHardened = true;
        const originalPlay = wavesurfer.play.bind(wavesurfer);
        wavesurfer.play = (...args: any[]) => {
            const result = originalPlay(...args);
            if (result && typeof result.catch === "function") {
                result.catch((err: any) => {
                    if (err.name !== "AbortError") throw err;
                });
            }
            return result;
        };
        // Pin pitch preservation explicitly. Browsers default to true, but the whole point of this
        // track is that speeding up must not change the speaker's voice, so do not rely on a default.
        forcePreservesPitch(wavesurfer?.backend?.media);
        guardSeekTo(wavesurfer);
        // ROUTE VOLUME THROUGH THE WEB AUDIO GAIN NODE, NOT THE ELEMENT.
        //
        // Every volume change used to end as `media.volume = value` — and iPadOS locks that
        // property (WebKit's m_volumeLocked): the write is accepted and ignored. Worse, with this
        // backend the audible stream is the createMediaElementSource capture, which honours only
        // the provider's volume — fed by exactly the locked path — and NOT element.muted, which
        // reaches only the already-silenced direct output. So on an iPad neither «только
        // озвучка» nor the volume slider could ever quieten the original.
        //
        // The backend's own gain node (source → analyser → gainNode → destination) attenuates
        // the audible stream on every platform. Volume goes there instead, and the element's
        // volume is pinned at 1 so desktop does not attenuate twice. Upstream wavesurfer fixed
        // this same iOS bug the same way (PR #2211), years after this copy was bundled.
        const backend = (wavesurfer as any)?.backend;
        if (backend && backend.gainNode && !backend.__gainRouted) {
            backend.__gainRouted = true;
            backend.setVolume = (value: number) => {
                try {
                    if (backend.media && backend.media.volume !== 1) backend.media.volume = 1;
                } catch {
                    /* locked (iPad) — already effectively 1 */
                }
                try {
                    backend.gainNode.gain.value = Number.isFinite(value) ? value : 1;
                } catch {
                    /* no gain to drive; the muted mirror still covers silence */
                }
            };
        }
    };

    const onLoading = ({ wavesurfer }: { wavesurfer: WaveSurfer }) => {
        harden(wavesurfer);
        setWavesurfer(wavesurfer);
    };

    /**
     * Harden, but do NOT adopt the instance.
     *
     * `setWavesurfer` is deliberately absent: nothing here drives playback through the instance,
     * and adopting it would newly activate the `seek` and `audioprocess` bindings further down —
     * which have been dormant for as long as peaks have been supplied, and which change what a
     * click on the wave does. Silencing a spurious rejection must not quietly restore behaviour
     * nobody asked for.
     */
    const onReady = ({ wavesurfer }: { wavesurfer: WaveSurfer }) => {
        harden(wavesurfer);
    };

    // Obtain drawing peaks once per source: from the editor server first, by decoding the mp3 in
    // the browser if the server cannot help (utils/originalPeaks). Aborted on unmount / source
    // change so switching folders quickly does not leave a request or a decode running.
    const mp3Url = state["mp3"];
    useEffect(() => {
        drawnWithoutRef.current = false;
        lateAppliedRef.current = false;
        restoreRef.current = null;
        setLatePeaks(null);
        if (!mp3Url) {
            settledRef.current = false;
            setPeaks([]);
            setPeaksSettled(false);
            return;
        }
        settledRef.current = false;
        const controller = new AbortController();
        let cancelled = false;
        const source = sourceRef.current;
        // PEAKS ARE HANDED OVER ONCE IN TIME, OR AT THE NEXT PAUSE.
        //
        // react-waves reloads the track whenever `audioPeaks` changes identity, and a reload
        // builds a NEW <audio>, discards the old one and seeks the replacement to the `pos` prop.
        // Doing that mid-playback throws away wherever the lecturer actually was: measured on the
        // deployed editor as the playhead dropping from 391.75s to 0.22s in one sample, and it
        // silently swallowed the seek a click had just performed on the element that was
        // discarded. So peaks that miss the deadline are no longer applied here: they are parked
        // in `latePeaks`, and the effect below draws them when nothing is playing, putting the
        // playhead back where it was (4119e839, папка 21, 02.10.2026 — before that they were
        // dropped, and a slow laptop showed no Russian track for the whole session).
        loadOriginalPeaks({
            mp3Url,
            mediaId: source.mediaId,
            folder: source.folder,
            liveStream: source.liveStream,
            signal: controller.signal,
            buckets: ORIGINAL_PEAKS_BUCKETS,
            playedDurationMs: () => sourceRef.current.audioDurationMs,
        }).then(({ peaks: result }) => {
            if (cancelled) return;
            if (!settledRef.current) {
                settledRef.current = true;
                setPeaks(result);
                setPeaksSettled(true);
                return;
            }
            // An empty result is a failed decode: nothing better to draw, leave the track alone.
            if (drawnWithoutRef.current && !lateAppliedRef.current && result.length > 0) {
                setLatePeaks(result);
            }
        });
        // Neither server nor decode in time must not cost the translator the whole track.
        const giveUp = window.setTimeout(() => {
            if (settledRef.current) return;
            settledRef.current = true;
            drawnWithoutRef.current = true;
            setPeaksSettled(true);
        }, PEAKS_DEADLINE_MS);
        return () => {
            cancelled = true;
            controller.abort();
            window.clearTimeout(giveUp);
        };
    }, [mp3Url]);

    /**
     * ДОРИСОВАТЬ ДОРОЖКУ, КОГДА НИЧЕГО НЕ ИГРАЕТ (4119e839, папка 21, 02.10.2026).
     *
     * Опоздавшие пики отдаются react-waves только тогда, когда лекция стоит: сразу, если пауза уже
     * сейчас, иначе на ближайшей паузе или остановке. Под играющей лекцией — никогда: перезагрузка
     * элемента посреди воспроизведения и есть тот прыжок 391,75 → 0,22 с. Один раз на источник.
     *
     * Перед перезагрузкой время читается со СТАРОГО элемента (wavesurfer его не знает — см.
     * trackMedia), в `pos` кладётся оно же, чтобы и собственный seek react-waves по готовности шёл
     * туда, а новый элемент возвращается на него эффектом ниже. React 17 (ReactDOM.render)
     * отрисовывает setState из эффекта синхронно, до следующего события, — щелчок «играть» не
     * может вклиниться между этой проверкой и перезагрузкой.
     */
    useEffect(() => {
        if (!latePeaks || lateAppliedRef.current) return;
        if (state.playing) return; // ждём паузы / остановки — эффект перезапустится
        const media = trackMedia();
        if (media && !media.paused) {
            // Состояние уже «стоп», а элемент ещё звучит: ждём, пока встанет и он.
            const recheck = () => setLateRecheck((n) => n + 1);
            media.addEventListener("pause", recheck, { once: true });
            return () => media.removeEventListener("pause", recheck);
        }
        lateAppliedRef.current = true;
        const at = media && Number.isFinite(media.currentTime) ? media.currentTime : null;
        restoreRef.current = { from: media, at };
        if (at !== null) setPos(at);
        setPeaks(latePeaks);
        setLatePeaks(null);
    }, [latePeaks, state.playing, lateRecheck]);

    /**
     * Новый элемент после перезагрузки — на место старого.
     *
     * react-waves меняет <audio> синхронно, во время отрисовки (UNSAFE_componentWillReceiveProps),
     * так что к этому эффекту новый элемент уже в контейнере. useLayoutEffect — чтобы время было
     * записано в той же синхронной задаче, что и перезагрузка: иначе таймер подгонки видео
     * (VideoPlayer/Player читает originalPositionMs) успел бы прочесть у нового элемента 0 и
     * дёрнуть картинку в начало папки. Короткий опрос — на случай, если элемента ещё нет.
     * Регистрация для расписания и зеркало `muted` подхватывают новый элемент своими эффектами
     * (в их зависимостях есть `peaks`).
     */
    useLayoutEffect(() => {
        const restore = restoreRef.current;
        if (!restore) return;
        let detach: (() => void) | null = null;
        const adopt = (): boolean => {
            const media = trackMedia();
            if (!media || media === restore.from) return false;
            restoreRef.current = null;
            forcePreservesPitch(media);
            if (restore.at !== null) detach = restorePlayhead(media, restore.at);
            return true;
        };
        if (adopt()) return () => detach?.();
        let tries = 0;
        const timer = window.setInterval(() => {
            tries += 1;
            if (adopt()) {
                window.clearInterval(timer);
            } else if (tries >= 25) {
                window.clearInterval(timer);
                restoreRef.current = null;
            }
        }, 200);
        return () => {
            window.clearInterval(timer);
            detach?.();
        };
    }, [peaks]);

    useEffect(() => {
        if (wavesurfer && wavesurfer.isReady) {
            wavesurfer.zoom();
        }
    }, [state.zoomLevel]);

    // Отслеживаем готовность, чтобы выполнить отложенный playStart-сик:
    // клик до загрузки волны иначе теряется, и беззвучные мастер-часы
    // играют с устаревшей позиции
    useEffect(() => {
        if (!wavesurfer) {
            return;
        }
        if (wavesurfer.isReady) {
            setWsReady(true);
            return;
        }
        // Новый (ещё не готовый) инстанс — например, после смены папки/медиа:
        // сбрасываем защёлку, иначе отложенный сик не сработает повторно
        setWsReady(false);
        const onReady = () => setWsReady(true);
        wavesurfer.on("ready", onReady);
        return () => {
            wavesurfer.un("ready", onReady);
        };
    }, [wavesurfer]);

    const onPosChange = (newPos: number) => {
        if (newPos !== pos) {
            setPos(newPos);
        }
    };

    const lastCallRef = useRef(0);
    const handleAudioProcess2 = useCallback(() => {
        const now = Date.now();
        if (now - lastCallRef.current >= 100) {
            const voicingSpaces = state.voicingSpacesAllPages[state.folder];
            lastCallRef.current = now;
            // Read the clock off the media element first. With backend
            // "MediaElementWebAudio" wavesurfer never reports ready and its own accessors are
            // unreliable (getDuration() answers 0 — see trackMedia below); the element itself
            // always knows. This value is the master clock the voicing corrects against, so it
            // has to be the one that is right.
            const media = wavesContainerRef.current?.querySelector("audio");
            const fromMedia = media && Number.isFinite(media.currentTime)
                ? media.currentTime * 1000
                : 0;
            let time = fromMedia || (wavesurfer?.getCurrentTime() || 0) * 1000;
            // if tiem include in voice space go to end ov voicing space
            if (state.voicingSpacesOn && wavesurfer?.isReady) {
                const hasSpase = voicingSpaces.some((space) => {
                    if (space.start < time && time < space.end && state.audioDuration) {
                        time = space.end;
                        wavesurfer?.seekTo(time / state.audioDuration);
                        return true;
                    }
                });
            }

            setPlaybackTime(time);
        }
    }, [
        wavesurfer,
        state.playingFragmentNext,
        state.playingFragment,
        handles.setPlayingFragment,
        handles.setPlayingFragmentNext,
        state.voicingSpaces,
        state.audioDuration,
    ]);

    // EVERY OUTSIDE REPOSITION COMES THROUGH HERE — and until now, none of them worked.
    //
    // Clicking a subtitle block, clicking a voicing, the resume shortcut: they all set playStart
    // and rely on this effect to move the original. It seeked through wavesurfer, gated on
    // isReady — which this backend never reports — so every one of those clicks silently left the
    // original where it was. The clicked voicing played once, but the master clock ran on from
    // the old position, and the schedule then continued the lecture from the wrong place.
    //
    // Seek the element itself; it is the thing that plays. The tolerance exists because
    // handleClick below seeks directly first and then sets playStart with the same value — by the
    // time this effect runs the element has advanced a few tens of milliseconds, and "correcting"
    // it backwards was an audible stutter on every wave click.
    useEffect(() => {
        if (typeof state.playStart !== "number") return;
        const media = trackMedia();
        if (!media || !Number.isFinite(media.currentTime)) return; // reruns when the track loads
        if (Math.abs(media.currentTime * 1000 - state.playStart) > 200) {
            seekTrackTo(state.playStart / 1000);
            announceUserSeek();
        }
        setPos(state.playStart / 1000);
        handles.setPlayStart(undefined);
    }, [state.playStart, wsReady, state.audioDuration]);

    /**
     * The element that actually plays this track.
     *
     * wavesurfer is created with backend "MediaElementWebAudio" and pre-computed audioPeaks, so it
     * never decodes the file: getDuration() answers 0 and isReady never turns true. Everything
     * that goes through wavesurfer — seekTo, the playStart effect, ReactWaves' own pos prop — is
     * gated on isReady, so none of it could move the playhead. Measured in the running editor, a
     * click sampled 0.05, 0.11, 0.21 … : playing from the very start, no seek at all.
     *
     * The media element itself is inside the waveform container and answers both duration and
     * currentTime correctly, so drive it directly and treat wavesurfer as decoration.
     */
    const trackMedia = (): HTMLMediaElement | null =>
        wavesContainerRef.current?.querySelector("audio") ?? null;

    /** Seconds of audio on this track, from whichever source actually knows. */
    const trackDurationSec = () => {
        const media = trackMedia();
        if (media && Number.isFinite(media.duration) && media.duration > 0) return media.duration;
        const fromWave = wavesurfer?.getDuration() || 0;
        return fromWave > 0 ? fromWave : (state.audioDuration || 0) / 1000;
    };

    const seekTrackTo = (seconds: number) => {
        const media = trackMedia();
        if (!media || !Number.isFinite(seconds) || seconds < 0) return;
        try {
            media.currentTime = seconds;
        } catch {
            // not seekable yet; the wavesurfer path still applies once it becomes ready
        }
    };

    const seek = (position: number) => {
        const currentTime = position * trackDurationSec();
        handles.setPlayStart(currentTime * 1000);
        if (state.subtitlesDataAll[state.originalLang]) {
            handles.setPlayOriginalStart(currentTime * 1000);
        }
    };
    /**
     * PUBLISH THE POSITION FOR THE INTERFACE.
     *
     * `playbackTime` drives the timecode, the highlighting and the drift trim — none of which
     * needs to be exact to the millisecond, and each update re-renders every cue in the folder.
     * At 100ms that cost was showing up where it hurts: a state write took several ticks to come
     * back, which is what let the schedule mistake its own instruction for someone else's. 200ms
     * is still four times finer than anything a reader can see on the timecode.
     *
     * Choosing which voicing sounds does NOT read this value: useVoicingSchedule reads the media
     * element directly, many times a second, because a cue boundary missed by 100ms is audible.
     * That separation is deliberate — this clock used to select fragments as well, and competing
     * with the other three selectors is what made voicings repeat and short ones vanish.
     *
     * The element is read directly here too. wavesurfer runs the "MediaElementWebAudio" backend,
     * which never reports ready and never emits `audioprocess`, so the event this used to be
     * bound to never fired: measured in the running editor, `playbackTime` was still 0 after 40s
     * of playback.
     */
    useEffect(() => {
        if (!state.playing) return;
        const tick = () => {
            const media = trackMedia();
            if (!media || media.paused || !Number.isFinite(media.currentTime)) return;
            handles.setPlaybackTime(media.currentTime * 1000);
        };
        // 500ms, and this number is load-bearing: every publish re-renders every row of the
        // folder, measured at ~200ms per render on a large folder. At 200ms the renders arrived
        // faster than they finished and the main thread never went idle — which starved every
        // timer in the page, the voicing schedule's included. The timecode does not need to be
        // finer than the eye reads it; the schedule does not read this value at all.
        const id = window.setInterval(tick, 500);
        tick();
        return () => window.clearInterval(id);
    }, [state.playing]);

    /**
     * SILENCE THAT WORKS ON AN iPAD.
     *
     * «Только озвучка» silences the original by setting its volume to 0, which reaches the
     * element as `media.volume = 0` (react-waves → wavesurfer MediaElement backend,
     * dist/index.es.js:2371). iPadOS WebKit ignores that assignment — HTMLMediaElement.volume is
     * read-only on iOS, by design — so for a translator on an iPad the toggle did nothing: the
     * tap played the voicing AND the Russian kept talking underneath, exactly as reported.
     *
     * `muted` is the property Apple does honour, and it silences the element on every platform,
     * including when the audio is routed through createMediaElementSource as it is here. So the
     * effective "must be silent" state is mirrored onto `muted`, and volume stays what it was —
     * on desktop the two agree, on iPad only this one actually works.
     *
     * Polled briefly because the element appears only when the waveform mounts, and the toggle
     * can already be on from the previous session.
     */
    const mustBeSilent = state.voiceoverOnlyPlayback || state.volumeOriginal === 0;
    useEffect(() => {
        const apply = (): boolean => {
            const media = trackMedia();
            if (!media) return false;
            if (media.muted !== mustBeSilent) media.muted = mustBeSilent;
            return true;
        };
        if (apply()) return;
        const timer = window.setInterval(() => {
            if (apply()) window.clearInterval(timer);
        }, 200);
        return () => window.clearInterval(timer);
        // `peaks`: опоздавшие пики перезагружают дорожку новым <audio> — зеркалить надо и его.
    }, [mustBeSilent, wavesurfer, state.mp3, peaks]);

    // Push the current volume through the (gain-routed) backend once it exists. react-waves does
    // the same on prop changes; this covers the value that was already current when the routing
    // was installed — the initial 40/60 mix a session starts with, which otherwise reached only
    // the dead element-volume path on an iPad. Idempotent with react-waves' own call.
    useEffect(() => {
        if (!wavesurfer) return;
        try {
            wavesurfer.setVolume((state.voiceoverOnlyPlayback ? 0 : state.volumeOriginal) / 100);
        } catch {
            /* backend not ready yet; react-waves re-applies on ready */
        }
    }, [wavesurfer, state.voiceoverOnlyPlayback, state.volumeOriginal]);

    // Hand the element to the schedule and to anything else that needs the true playhead.
    // Polled because the element is not always in the document when wavesurfer reports itself.
    // Re-run on `peaks` too: late peaks reload the track into a NEW element (see above).
    useEffect(() => {
        const found = trackMedia();
        if (found) {
            registerOriginalMedia(found);
            return;
        }
        const timer = window.setInterval(() => {
            const media = trackMedia();
            if (!media) return;
            registerOriginalMedia(media);
            window.clearInterval(timer);
        }, 200);
        return () => window.clearInterval(timer);
    }, [wavesurfer, state.mp3, peaks]);

    // Remember where the lecturer was left, for Ctrl+I. Read off the element: this backend never
    // reports ready and wavesurfer.getCurrentTime() answers 0, so the saved position was always
    // the very beginning of the folder.
    useEffect(() => {
        if (state.playing) return;
        const currentTime = trackMedia()?.currentTime;
        if (currentTime) {
            localStorage.setItem(LocalStorageKeys.pausedTime, (currentTime * 1000).toString());
        }
    }, [state.playing]);

    useEffect(() => {
        if (wavesurfer) {
            wavesurfer.on("audioprocess", handleAudioProcess2);
            wavesurfer.on("seek", seek);
        }
        return () => {
            if (wavesurfer) {
                wavesurfer.un("audioprocess", handleAudioProcess2);
                wavesurfer.un("seek", seek);
            }
        };
    }, [wavesurfer, handleAudioProcess2]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        const duration = trackDurationSec();

        // The track LOOKS vertical but is not: AudioPlayerWrapperSC is laid out horizontally,
        // `width` the whole timeline, and then rotated 90deg (styled.ts). offsetX is reported in
        // the element's own, unrotated frame, so the horizontal offset IS the moment pointed at —
        // "fixing" this to offsetY broke clicking on the wave entirely.
        const relativeX =
            e.nativeEvent.offsetX / (e.currentTarget.offsetWidth || wavesContainerRef.current?.offsetWidth || 1);
        const seconds = relativeX * duration;
        const relative = relativeX;
        // Move the playhead first and unconditionally; the calls below only take effect once
        // wavesurfer decides it is ready, which with this backend it never does.
        seekTrackTo(seconds);
        // Tell the schedule this was a person, not one of its own writes landing late — it
        // treats the two very differently (see utils/playbackIntent).
        announceUserSeek();
        wavesurfer?.isReady && wavesurfer?.seekTo(relative);
        setPos(seconds);
        handles.setPlayStart(seconds * 1000);

        // Dispatch time for CreateSubtitleByInterval pick mode
        const timeMs = seconds * 1000;
        window.dispatchEvent(new CustomEvent("waveform-time-click", { detail: { timeMs } }));

        // PLAY THE VOICEOVER THAT COVERS THIS MOMENT, not whichever was clicked
        // last.
        //
        // Clearing playingFragment moved the Russian playhead but told the
        // translated side nothing, so it kept the fragment already selected: a
        // click at 4:10 played back whatever had been chosen minutes earlier.
        // The two tracks are meant to be the same moment in two languages, and
        // that is exactly what stopped being true.
        //
        // Fragments are positioned on the ORIGINAL timeline (start / length in
        // ms against the Russian audio), so the clicked millisecond selects one
        // directly. Nothing covering it -- a gap between cues -- clears the
        // selection as before, rather than leaving a stale one playing.
        // TELL THE TRANSLATED SIDE *WHERE* IN TIME WE ARE, not just which cue.
        //
        // Naming the cue was half the job. useAudioPlay only picks a fragment and seeks it to
        // the right offset when `playOriginalStart` is set AND `playingFragment` is empty:
        //
        //     state.playing && !state.playingFragment && state.playOriginalStart && ...
        //
        // This handler set the fragment and never set playOriginalStart, so that block was
        // skipped twice over. The chosen cue then played from wherever its own playhead
        // happened to be — normally its beginning — instead of from the clicked millisecond.
        // Click halfway through a cue and the voicing restarted the sentence.
        //
        // Publishing the time and leaving the fragment empty lets useAudioPlay do both jobs:
        // it selects the cue covering this instant and seeks it to (t - start) / length. That
        // also keeps the property the previous fix was after — the cue is chosen from the
        // clicked moment, never from a stale selection — because it is chosen BY time.
        handles.setPlayOriginalStart(timeMs);
        handles.setPlayingFragment(null);
        handles.setPlaying(true);
        handles.setVoiceoverOnlyPlayback(false);
        handles.setPlaySavedTrack(false);
        handles.setStartPlaying(true);
    };

    return (
        <>
            <AudioPlayerContainerSC>
                <AudioPlayerWrapperSC
                    length={state.durationFull ? state.durationFull / zoomLevel : null}
                >
                    <div
                        className="x_player_vert"
                        ref={wavesContainerRef}
                        style={{
                            overflowY: "hidden",

                            whiteSpace: "nowrap",
                            borderRight:
                                state.durationFull > state.audioDuration
                                    ? "2px dotted red"
                                    : "none",
                        }}
                    >
                        {(!state.mp3 || !peaksSettled) && (
                            <div style={{ padding: 14 }}>Loading...</div>
                        )}

                        {state.mp3 && peaksSettled && (
                            <WaveWrapperSC>
                                <ReactWaves
                                    audioFile={state["mp3"]}
                                    className={"react-waves"}
                                    audioPeaks={peaks}
                                    options={{
                                        barGap: 1,
                                        barWidth: 2,
                                        barHeight: 2,
                                        // barRadius: 3,
                                        cursorWidth: 0,
                                        height: 100,
                                        hideScrollbar: true,
                                        progressColor: "#ff7606",
                                        responsive: false,
                                        waveColor: "#D1D6DA",
                                        // Play through an HTML5 media element, NOT an
                                        // AudioBufferSourceNode. The WebAudio backend (WaveSurfer's
                                        // default when `backend` is omitted) applies audioRate by
                                        // resampling, which shifts pitch — speeding the slider up
                                        // chipmunked the speaker. A media element time-stretches and
                                        // keeps the pitch. Peaks come from `audioPeaks` above,
                                        // because this backend never decodes.
                                        backend: "MediaElementWebAudio",
                                        audioRate: state.playbackRate,
                                    }}
                                    // zoom={zoomLevelLocal}
                                    // zoom={100 - zoomLevel * ((90 - zoomLevel) / 10)}
                                    zoom={zoomLevel / 100}
                                    // zoom={1}
                                    volume={
                                        (state.voiceoverOnlyPlayback
                                            ? 0
                                            : state["volumeOriginal"]) / 100
                                    }
                                    pos={pos}
                                    playing={state["playing"] && !held}
                                    onPosChange={onPosChange}
                                    onLoading={onLoading}
                                    onReady={onReady}
                                />
                            </WaveWrapperSC>
                        )}
                    </div>
                </AudioPlayerWrapperSC>

                <AudioPlayerWrapperSC
                    length={state.durationFull ? state.durationFull / zoomLevel : null}
                    onClick={handleClick}
                />
            </AudioPlayerContainerSC>
        </>
    );
}
